import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CheckCircle2, ClipboardEdit, ShieldCheck, AlertTriangle, Send, Lock, TreePine } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { getMyRegistration, updateMyRegistration, submitMyRegistration } from "@/lib/api";

const STATUS_META = {
  draft:      { icon: ClipboardEdit,  tone: "yellow", key: "draft" },
  submitted:  { icon: Send,           tone: "orange", key: "submitted" },
  verified:   { icon: ShieldCheck,    tone: "green",  key: "verified" },
  approved:   { icon: CheckCircle2,   tone: "green",  key: "approved" },
  rejected:   { icon: AlertTriangle,  tone: "red",    key: "rejected" },
};

const INDIAN_STATES = [
  "Andhra Pradesh", "Bihar", "Chhattisgarh", "Gujarat", "Haryana", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Odisha", "Punjab", "Rajasthan",
  "Tamil Nadu", "Telangana", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Other",
];

export default function FarmerDashboard() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [orchards, setOrchards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const load = async () => {
    try {
      const r = await getMyRegistration();
      setProfile(r.profile);
      setOrchards(r.orchards || []);
      setForm({
        full_name: r.profile.full_name || "",
        alternate_phone: r.profile.alternate_phone || "",
        email: r.profile.email || "",
        village: r.profile.village || "",
        taluka: r.profile.taluka || "",
        district: r.profile.district || "Jalna",
        state: r.profile.state || "Maharashtra",
        postal_code: r.profile.postal_code || "",
        address_line: r.profile.address_line || "",
        fpo_name: r.profile.fpo_name || "",
        aadhaar_last4: r.profile.aadhaar_last4 || "",
        bank_account_last4: r.profile.bank_account_last4 || "",
        bank_name: r.profile.bank_name || "",
        ifsc: r.profile.ifsc || "",
        land_total_acres: r.profile.land_total_acres || "",
        primary_commodity: r.profile.primary_commodity || "Jalna Sweet Lime (Mosambi)",
        experience_years: r.profile.experience_years || "",
        notes: r.profile.notes || "",
        consent_accepted: !!r.profile.consent_accepted,
      });
    } catch (e) {
      toast.error(t("toast.generic_error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const isEditable = profile && (profile.registration_status === "draft" || profile.registration_status === "rejected");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const buildPayload = () => ({
    ...form,
    email: form.email || null,
    postal_code: form.postal_code || null,
    address_line: form.address_line || null,
    alternate_phone: form.alternate_phone || null,
    fpo_name: form.fpo_name || null,
    aadhaar_last4: form.aadhaar_last4 || null,
    bank_account_last4: form.bank_account_last4 || null,
    bank_name: form.bank_name || null,
    ifsc: form.ifsc || null,
    land_total_acres: form.land_total_acres ? parseFloat(form.land_total_acres) : null,
    experience_years: form.experience_years ? parseInt(form.experience_years) : null,
    notes: form.notes || null,
  });

  const saveDraft = async () => {
    setSaving(true);
    try {
      const r = await updateMyRegistration(buildPayload());
      setProfile(r.profile);
      toast.success(t("farmer_reg.save_ok"));
    } catch (err) {
      toast.error(err?.response?.data?.detail?.error || t("toast.generic_error"));
    } finally { setSaving(false); }
  };

  const submitForReview = async () => {
    if (!form.consent_accepted) { toast.error(t("farmer_reg.submit_consent")); return; }
    setSaving(true);
    try {
      await updateMyRegistration(buildPayload());
      const r = await submitMyRegistration();
      setProfile(r.profile);
      toast.success(t("farmer_reg.submit_ok"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "missing_fields") toast.error(`Missing: ${detail.fields.join(", ")}`);
      else if (detail?.error === "consent_required") toast.error(t("farmer_reg.submit_consent"));
      else toast.error(t("toast.generic_error"));
    } finally { setSaving(false); }
  };

  if (loading || !profile || !form) {
    return <div className="max-w-5xl mx-auto px-6 py-24 text-center text-[var(--tr-muted)]">{t("common.loading")}</div>;
  }

  const status = profile.registration_status || "approved";
  const meta = STATUS_META[status] || STATUS_META.draft;
  const StatusIcon = meta.icon;
  const toneClass = meta.tone === "green" ? "bg-[var(--tr-lime)]/25 text-[var(--tr-deep-green)]"
    : meta.tone === "orange" ? "bg-[var(--tr-zest)]/20 text-[var(--tr-zest)]"
    : meta.tone === "yellow" ? "bg-[var(--tr-citrus-yellow)]/25 text-[var(--tr-deep-green)]"
    : "bg-red-100 text-red-700";

  return (
    <div data-testid="page-farmer-dashboard" className="max-w-5xl mx-auto px-6 lg:px-10 pt-16 pb-24">
      {/* Header */}
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="tr-eyebrow">{t("farmer_reg.dashboard_title")}</div>
          <h1 className="tr-section-title text-4xl lg:text-5xl mt-3">
            {t("farmer_reg.welcome")}, {profile.full_name || user?.full_name || "किसान"}
          </h1>
          <div className="mt-3 text-[var(--tr-muted)] text-sm">
            {t("farmer_reg.farmer_id")}:{" "}
            <span className="font-mono font-bold text-[var(--tr-deep-green)]" data-testid="farmer-code">
              {profile.farmer_code || t("farmer_reg.no_code")}
            </span>
          </div>
        </div>
        <button onClick={logout} data-testid="farmer-logout" className="text-sm underline text-[var(--tr-muted)]">
          {t("auth.logout")}
        </button>
      </div>

      {/* Status card */}
      <div className={`mt-8 rounded-3xl p-6 md:p-8 border ${meta.tone === "red" ? "border-red-200 bg-red-50" : "border-[rgba(6,78,59,0.12)] bg-white"}`} data-testid={`status-card-${status}`}>
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.18em] ${toneClass}`}>
            <StatusIcon className="w-4 h-4" /> {t(`farmer_reg.status_${meta.key}`)}
          </span>
          <span className="text-xs text-[var(--tr-muted)] uppercase tracking-[0.18em]">{t("farmer_reg.status_label")}</span>
        </div>
        <p className="mt-4 text-[var(--tr-deep-green)] leading-relaxed">{t(`farmer_reg.status_${meta.key}_help`)}</p>
        {status === "rejected" && profile.rejection_reason && (
          <div className="mt-4 rounded-xl border border-red-200 bg-white p-4" data-testid="rejection-reason">
            <div className="text-xs uppercase tracking-[0.18em] text-red-600 font-bold">{t("farmer_reg.reason_label")}</div>
            <div className="mt-1 text-[var(--tr-deep-green)]">{profile.rejection_reason}</div>
          </div>
        )}
      </div>

      {/* Read-only notice */}
      {!isEditable && status !== "approved" && (
        <div className="mt-4 text-sm text-[var(--tr-muted)] flex items-center gap-2" data-testid="readonly-note">
          <Lock className="w-4 h-4" /> {t("farmer_reg.read_only_note")}
        </div>
      )}

      {/* Registration form */}
      <div className="mt-8 space-y-8">
        <Section title={t("farmer_reg.section_personal")}>
          <TwoCol>
            <Fld label={t("farmer_reg.f_full_name")}><input disabled={!isEditable} data-testid="reg-full-name" className="tr-input" value={form.full_name} onChange={set("full_name")} /></Fld>
            <Fld label={t("farmer_reg.f_alt_phone")}><input disabled={!isEditable} inputMode="tel" data-testid="reg-alt-phone" className="tr-input" value={form.alternate_phone} onChange={set("alternate_phone")} /></Fld>
            <Fld label={t("farmer_reg.f_email")} className="md:col-span-2"><input disabled={!isEditable} type="email" data-testid="reg-email" className="tr-input" value={form.email} onChange={set("email")} /></Fld>
          </TwoCol>
        </Section>

        <Section title={t("farmer_reg.section_address")}>
          <TwoCol>
            <Fld label={t("farmer_reg.f_village")}><input disabled={!isEditable} data-testid="reg-village" className="tr-input" value={form.village} onChange={set("village")} /></Fld>
            <Fld label={t("farmer_reg.f_taluka")}><input disabled={!isEditable} data-testid="reg-taluka" className="tr-input" value={form.taluka} onChange={set("taluka")} /></Fld>
            <Fld label={t("farmer_reg.f_district")}><input disabled={!isEditable} data-testid="reg-district" className="tr-input" value={form.district} onChange={set("district")} /></Fld>
            <Fld label={t("farmer_reg.f_state")}>
              <select disabled={!isEditable} data-testid="reg-state" className="tr-input" value={form.state} onChange={set("state")}>
                {INDIAN_STATES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </Fld>
            <Fld label={t("farmer_reg.f_postal")}><input disabled={!isEditable} data-testid="reg-postal" className="tr-input" value={form.postal_code} onChange={set("postal_code")} /></Fld>
            <Fld label={t("farmer_reg.f_fpo")}><input disabled={!isEditable} data-testid="reg-fpo" className="tr-input" value={form.fpo_name} onChange={set("fpo_name")} /></Fld>
            <Fld label={t("farmer_reg.f_address")} className="md:col-span-2"><input disabled={!isEditable} data-testid="reg-address" className="tr-input" value={form.address_line} onChange={set("address_line")} /></Fld>
          </TwoCol>
        </Section>

        <Section title={t("farmer_reg.section_farm")}>
          <TwoCol>
            <Fld label={t("farmer_reg.f_land")}><input disabled={!isEditable} type="number" step="0.1" data-testid="reg-land" className="tr-input" value={form.land_total_acres} onChange={set("land_total_acres")} /></Fld>
            <Fld label={t("farmer_reg.f_primary")}><input disabled={!isEditable} data-testid="reg-primary" className="tr-input" value={form.primary_commodity} onChange={set("primary_commodity")} /></Fld>
            <Fld label={t("farmer_reg.f_experience")}><input disabled={!isEditable} type="number" data-testid="reg-experience" className="tr-input" value={form.experience_years} onChange={set("experience_years")} /></Fld>
            <Fld label={t("farmer_reg.f_notes")} className="md:col-span-2"><textarea disabled={!isEditable} data-testid="reg-notes" className="tr-input tr-textarea" value={form.notes} onChange={set("notes")} /></Fld>
          </TwoCol>
        </Section>

        <Section title={t("farmer_reg.section_kyc")}>
          <TwoCol>
            <Fld label={t("farmer_reg.f_aadhaar_last4")}><input disabled={!isEditable} maxLength={4} inputMode="numeric" data-testid="reg-aadhaar" className="tr-input" value={form.aadhaar_last4} onChange={(e) => setForm((f) => ({...f, aadhaar_last4: e.target.value.replace(/\D/g,"").slice(0,4)}))} /></Fld>
          </TwoCol>
        </Section>

        {/* Round 3 Part A guardrail — NO bank details field anywhere on the site. Payout details are captured internally by the ops team, never in a public form. */}

        <Section title={t("farmer_reg.section_consent")}>
          <div className="rounded-2xl border border-[rgba(6,78,59,0.15)] bg-[var(--tr-bone)] p-5 text-sm text-[var(--tr-deep-green)] leading-relaxed">
            {t("farmer_reg.consent_text")}
          </div>
          <label className="mt-4 flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              disabled={!isEditable}
              data-testid="reg-consent"
              checked={form.consent_accepted}
              onChange={(e) => setForm((f) => ({ ...f, consent_accepted: e.target.checked }))}
              className="mt-1 w-5 h-5 accent-[var(--tr-deep-green)]"
            />
            <span className="text-sm font-semibold text-[var(--tr-deep-green)]">{t("farmer_reg.consent_accept")}</span>
          </label>
        </Section>

        {/* Actions */}
        {isEditable && (
          <div className="flex flex-wrap gap-4">
            <button onClick={saveDraft} disabled={saving} data-testid="reg-save-draft" className="tr-pill-btn tr-btn-outline disabled:opacity-60">
              {saving ? t("common.submitting") : t("farmer_reg.save_draft")}
            </button>
            <button onClick={submitForReview} disabled={saving} data-testid="reg-submit" className="tr-pill-btn tr-btn-primary disabled:opacity-60">
              {saving ? t("common.submitting") : t("farmer_reg.submit_now")}
            </button>
          </div>
        )}
      </div>

      {/* Orchards section */}
      <div className="mt-16">
        <h2 className="tr-section-title text-2xl flex items-center gap-3">
          <TreePine className="w-6 h-6" /> {t("farmer_reg.orchards_title")}
        </h2>
        {status !== "approved" ? (
          <div className="mt-4 tr-card p-8 text-[var(--tr-muted)]" data-testid="orchards-locked">
            <Lock className="w-5 h-5 mb-3 text-[var(--tr-muted)]" />
            {t("farmer_reg.orchards_locked")}
          </div>
        ) : orchards.length === 0 ? (
          <div className="mt-4 tr-card p-8 text-[var(--tr-muted)]" data-testid="orchards-empty">
            {t("farmer_reg.orchards_empty")}
          </div>
        ) : (
          <div className="mt-4 tr-card overflow-hidden" data-testid="orchards-list">
            <table className="w-full text-sm">
              <thead className="bg-[var(--tr-sage)]/50">
                <tr>
                  {["Orchard ID", "Area (Acres)", "Trees", "Bahar", "Harvest"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-semibold text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orchards.map((o) => (
                  <tr key={o.id} className="border-t border-[rgba(6,78,59,0.08)]">
                    <td className="px-4 py-3 font-mono font-semibold text-[var(--tr-deep-green)]">{o.orchard_code}</td>
                    <td className="px-4 py-3">{o.area_acres ?? o.area_raw ?? "—"}</td>
                    <td className="px-4 py-3">{o.tree_count ?? "—"}</td>
                    <td className="px-4 py-3">{o.bahar ?? "—"}</td>
                    <td className="px-4 py-3">{o.harvest_month ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="tr-card p-6 md:p-8">
      <h3 className="font-display font-extrabold text-lg text-[var(--tr-deep-green)] mb-4">{title}</h3>
      {children}
    </div>
  );
}

function TwoCol({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>;
}

function Fld({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="tr-label">{label}</span>
      {children}
    </label>
  );
}
